import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authFetch, API_URL } from '../utils/authFetch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import {
  Users, Shield, Activity, Settings, AlertTriangle,
  Trash2, ChevronDown, BarChart3, Clock, Lock, Zap,
  ArrowLeft, RefreshCw, CheckCircle
} from 'lucide-react';

const ROLES = ['admin', 'analyst', 'viewer'];
const ROLE_COLORS = {
  admin: 'bg-red-500/20 text-red-400 border-red-500/30',
  analyst: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  viewer: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  user: 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30'
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, permissions } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changingRole, setChangingRole] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, statsRes, logRes] = await Promise.all([
        authFetch(`${API_URL}/api/rbac/users`).then(r => r.json()),
        authFetch(`${API_URL}/api/rbac/system-stats`).then(r => r.json()),
        authFetch(`${API_URL}/api/rbac/activity-log?limit=30`).then(r => r.json())
      ]);
      setUsers(usersRes.users || []);
      setStats(statsRes);
      setActivityLog(logRes.logs || []);
    } catch (error) {
      console.error('Admin data fetch failed:', error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Redirect non-admins
  if (permissions && permissions.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-neutral-400 mb-6">Only administrators can access this panel.</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const handleRoleChange = async (userId, newRole) => {
    setChangingRole(userId);
    try {
      await authFetch(`${API_URL}/api/rbac/update-role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, role: newRole })
      });
      fetchData();
    } catch (error) { console.error('Role change failed:', error.message); }
    finally { setChangingRole(null); }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await authFetch(`${API_URL}/api/rbac/users/${userId}`, { method: 'DELETE' });
      fetchData();
    } catch (error) { console.error('Delete user failed:', error.message); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      <SEO title="Admin Panel | SecureSphere" description="Manage users, roles, and system settings" />

      {/* Header */}
      <div className="bg-[#242424] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition" data-testid="admin-home-btn">
              <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-9 w-9 rounded-lg" />
              <span className="text-lg font-bold hidden sm:inline"><span className="text-white">Infuse</span><span className="text-[#F26419]">.AI</span><span className="text-neutral-400 text-xs align-top">™</span></span>
            </button>
            <div className="h-6 w-px bg-white/10 hidden sm:block" />
            <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-white/5 rounded-lg transition" title="Back to Dashboard">
              <ArrowLeft className="w-5 h-5 text-neutral-400" />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Settings className="w-5 h-5 text-orange-400" /> Admin Panel
              </h1>
              <p className="text-sm text-neutral-500">Manage users, roles & system</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
            <Badge className="bg-red-500/20 text-red-400">{user?.email}</Badge>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex gap-1">
          {[
            { id: 'users', label: 'User Management', icon: Users },
            { id: 'stats', label: 'System Stats', icon: BarChart3 },
            { id: 'activity', label: 'Activity Log', icon: Clock },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 ${activeTab === tab.id ? 'text-orange-400 border-orange-400' : 'text-neutral-500 border-transparent hover:text-white'}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* USER MANAGEMENT */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-[#242424] border-white/10">
                <CardContent className="p-4">
                  <Users className="w-5 h-5 text-blue-400 mb-2" />
                  <p className="text-2xl font-bold text-white">{stats?.users?.total || 0}</p>
                  <p className="text-xs text-neutral-400">Total Users</p>
                </CardContent>
              </Card>
              {Object.entries(stats?.users?.by_role || {}).map(([role, count]) => (
                <Card key={role} className="bg-[#242424] border-white/10">
                  <CardContent className="p-4">
                    <Shield className="w-5 h-5 text-orange-400 mb-2" />
                    <p className="text-2xl font-bold text-white">{count}</p>
                    <p className="text-xs text-neutral-400 capitalize">{role}s</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-[#242424] border-white/10">
              <CardHeader><CardTitle className="text-white">All Users</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 text-left">
                        <th className="pb-3 text-sm text-neutral-400 font-medium">User</th>
                        <th className="pb-3 text-sm text-neutral-400 font-medium">Role</th>
                        <th className="pb-3 text-sm text-neutral-400 font-medium">Joined</th>
                        <th className="pb-3 text-sm text-neutral-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => {
                        const uid = u._id || u.id;
                        const isCurrentUser = uid === (user?.id || user?._id);
                        return (
                          <tr key={uid} className="border-b border-white/5 hover:bg-white/5 transition">
                            <td className="py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#F7B731] to-[#F26419] flex items-center justify-center text-black font-bold text-sm">
                                  {(u.name || u.email)?.[0]?.toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-white font-medium text-sm">{u.name || 'Unknown'}</p>
                                  <p className="text-xs text-neutral-500">{u.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3">
                              <div className="relative inline-block">
                                <select
                                  data-testid={`role-select-${uid}`}
                                  value={u.role || 'user'}
                                  onChange={(e) => handleRoleChange(uid, e.target.value)}
                                  disabled={isCurrentUser || changingRole === uid}
                                  className={`appearance-none px-3 py-1.5 pr-7 rounded-lg text-xs font-medium border cursor-pointer ${ROLE_COLORS[u.role || 'user']} bg-transparent ${isCurrentUser ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  {ROLES.map(r => <option key={r} value={r} className="bg-[#242424] text-white">{r}</option>)}
                                </select>
                                <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none" />
                              </div>
                            </td>
                            <td className="py-3 text-sm text-neutral-400">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                            </td>
                            <td className="py-3">
                              {!isCurrentUser && (
                                <button onClick={() => handleDeleteUser(uid, u.email)} className="p-1.5 hover:bg-red-500/20 rounded-lg transition text-neutral-500 hover:text-red-400" title="Delete user">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                              {isCurrentUser && <span className="text-xs text-neutral-600">You</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* SYSTEM STATS */}
        {activeTab === 'stats' && stats && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white flex items-center gap-2"><Activity className="w-5 h-5 text-orange-400" /> API Performance</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { label: 'Requests (24h)', value: stats.api_stats.total_requests_24h.toLocaleString(), color: 'text-blue-400' },
                    { label: 'Avg Response', value: `${stats.api_stats.avg_response_ms}ms`, color: 'text-green-400' },
                    { label: 'Error Rate', value: `${stats.api_stats.error_rate_percent}%`, color: 'text-red-400' },
                    { label: 'Peak RPS', value: stats.api_stats.peak_rps.toLocaleString(), color: 'text-orange-400' },
                  ].map((s) => (
                    <div key={s.label} className="flex items-center justify-between">
                      <span className="text-sm text-neutral-400">{s.label}</span>
                      <span className={`font-bold ${s.color}`}>{s.value}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white flex items-center gap-2"><Users className="w-5 h-5 text-blue-400" /> Users</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-400">Total Users</span>
                    <span className="font-bold text-white">{stats.users.total}</span>
                  </div>
                  {Object.entries(stats.users.by_role).map(([role, count]) => (
                    <div key={role} className="flex items-center justify-between">
                      <span className="text-sm text-neutral-400 capitalize">{role}s</span>
                      <Badge className={ROLE_COLORS[role]}>{count}</Badge>
                    </div>
                  ))}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-400">Active Sessions</span>
                    <span className="font-bold text-green-400">{stats.api_stats.active_sessions}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#242424] border-white/10">
                <CardHeader><CardTitle className="text-white flex items-center gap-2"><Shield className="w-5 h-5 text-red-400" /> Security</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-400">Failed Logins (24h)</span>
                    <span className="font-bold text-red-400">{stats.security.failed_logins_24h}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-400">Locked Accounts</span>
                    <span className="font-bold text-orange-400">{stats.security.locked_accounts}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-400">Active Tokens</span>
                    <span className="font-bold text-green-400">{stats.security.tokens_active}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ACTIVITY LOG */}
        {activeTab === 'activity' && (
          <Card className="bg-[#242424] border-white/10">
            <CardHeader><CardTitle className="text-white">Login Activity</CardTitle></CardHeader>
            <CardContent>
              {activityLog.length === 0 ? (
                <p className="text-neutral-500 text-center py-8">No activity logged yet. Activity will appear after user logins.</p>
              ) : (
                <div className="space-y-3">
                  {activityLog.map((log) => (
                    <div key={log.timestamp || log.email} className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <div>
                          <p className="text-sm text-white">{log.email}</p>
                          <p className="text-xs text-neutral-500">IP: {log.ip}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-neutral-300 border-white/20 capitalize">{log.action}</Badge>
                        <p className="text-xs text-neutral-500 mt-1">{log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
